import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const MembersList = ({ members, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [loading, setLoading] = useState(false);

  const filteredMembers = members?.filter(member => {
    const matchesSearch = member?.user_profiles?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         member?.user_profiles?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesRole = filterRole === 'all' || member?.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      gym_owner: 'bg-primary/10 text-primary',
      trainer: 'bg-accent/10 text-accent',
      staff: 'bg-warning/10 text-warning',
      member: 'bg-success/10 text-success'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      basic: 'bg-muted text-muted-foreground',
      premium: 'bg-primary/10 text-primary',
      elite: 'bg-accent/10 text-accent'
    };
    return colors?.[plan] || 'bg-muted text-muted-foreground';
  };

  const handleToggleStatus = async (memberId, currentStatus) => {
    setLoading(true);
    try {
      const { error } = await supabase?.from('tenant_members')?.update({ is_active: !currentStatus })?.eq('id', memberId);

      if (error) throw error;
      onRefresh?.();
    } catch (err) {
      console.error('Failed to update member status:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-heading font-bold text-foreground flex items-center gap-2">
          <Icon name="Users" size={24} color="var(--color-primary)" />
          Members
        </h2>
        <Button onClick={onRefresh} variant="ghost" size="sm" disabled={loading}>
          <Icon name="RefreshCw" size={18} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>
      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Icon name="Search" size={18} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e?.target?.value)}
          className="px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
        >
          <option value="all">All Roles</option>
          <option value="gym_owner">Gym Owner</option>
          <option value="trainer">Trainer</option>
          <option value="staff">Staff</option>
          <option value="member">Member</option>
        </select>
      </div>
      {/* Members List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {filteredMembers?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No members found</p>
          </div>
        ) : (
          filteredMembers?.map((member) => (
            <div
              key={member?.id}
              className="flex items-center justify-between p-4 bg-background rounded-lg border border-border hover:border-primary transition-smooth"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Icon name="User" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">
                    {member?.user_profiles?.full_name || 'Unknown'}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {member?.user_profiles?.email}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getRoleBadgeColor(member?.role)}`}>
                      {member?.role?.replace('_', ' ')}
                    </span>
                    {member?.membership_plan && (
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPlanBadgeColor(member?.membership_plan)}`}>
                        {member?.membership_plan}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggleStatus(member?.id, member?.is_active)}
                  disabled={loading}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-smooth ${
                    member?.is_active
                      ? 'bg-success/10 text-success hover:bg-success/20' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {member?.is_active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MembersList;
